/**
 * useDashboardLayout — persönliches Dashboard-Layout des angemeldeten Admins.
 * Optimistisch: die Oberfläche folgt sofort, gespeichert wird im Profil
 * (agents.einstellungen); ohne Migration bleibt es auf diesem Gerät.
 */
import { useCallback, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/api/AuthContext';
import { userSettingsStore } from '@/lib/userSettings';
import {
  DEFAULT_DASHBOARD_LAYOUT, isDefaultLayout, moveWidget, normalizeDashboardLayout, setWidgetVisible,
} from '@/lib/dashboardLayout';

const STALE_MS = 5 * 60 * 1000;
export const userSettingsKey = (userId) => ['user-settings', userId];

export function useDashboardLayout() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const key = userSettingsKey(user?.id);
  const warnedLocal = useRef(false);

  const settingsQ = useQuery({
    queryKey: key,
    queryFn: () => userSettingsStore.load(user),
    enabled: !!user?.id,
    staleTime: STALE_MS,
  });

  const layout = useMemo(() => normalizeDashboardLayout(settingsQ.data?.dashboard), [settingsQ.data]);

  const saveM = useMutation({
    mutationFn: (next) => userSettingsStore.save(user, { dashboard: next }),
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData(key);
      queryClient.setQueryData(key, (old) => ({ ...(old || {}), dashboard: next }));
      return { previous };
    },
    onError: (err, _next, ctx) => {
      queryClient.setQueryData(key, ctx?.previous);
      toast.error(`Dashboard-Einstellung nicht gespeichert: ${err.message}`);
    },
    onSuccess: (result) => {
      queryClient.setQueryData(key, result.settings);
      if (result.persisted === 'local' && !warnedLocal.current) {
        warnedLocal.current = true;
        toast.warning('Nur auf diesem Gerät gespeichert', {
          description: 'Datenbank-Update (Migration 20261103_01) fehlt noch – danach gilt das Layout auf allen Geräten.',
        });
      }
    },
  });

  const commit = useCallback((next) => saveM.mutate(next), [saveM]);

  return {
    layout,
    loading: settingsQ.isLoading,
    saving: saveM.isPending,
    isDefault: isDefaultLayout(layout),
    setVisible: (id, visible) => commit(setWidgetVisible(layout, id, visible)),
    move: (id, delta) => commit(moveWidget(layout, id, delta)),
    reset: () => commit(DEFAULT_DASHBOARD_LAYOUT),
  };
}
